import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faUpload } from "@fortawesome/free-solid-svg-icons";
import { uploadSpaceMedia } from "@/utils/supabaseStorage";
import { Switch } from "@/components/ui/switch";

export default function FileConfigForm({
  config,
  onUpdate,
  widgetId,
  spaceId,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const updateFile = (field, value) => {
    if (typeof onUpdate !== "function") {
      console.error("onUpdate is not a function", { onUpdate });
      return;
    }
    const newFile = { ...(config.file || {}), [field]: value };
    onUpdate({ file: newFile });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds the maximum allowed size (10MB)");
      return;
    }

    // Check file type - only allow safe web file types
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
      "text/plain",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError(
        "Unsupported file type. Please upload PDF, DOCX, XLSX, PPTX, TXT, or CSV files."
      );
      return;
    }

    try {
      setIsUploading(true);
      const fileUrl = await uploadSpaceMedia(file, spaceId, widgetId);

      // Update the file data
      onUpdate({
        file: {
          ...(config.file || {}),
          url: fileUrl,
          name: file.name,
          type: file.type,
          size: file.size,
        },
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // Get the current preview setting or default to true
  const showPreview = config.file?.showPreview !== false;

  // Toggle preview setting
  const handlePreviewToggle = (checked) => {
    updateFile("showPreview", checked);
  };

  return (
    <div className="space-y-4 w-full">
      <div>
        <Label className="text-white mb-2">Document File</Label>
        <div className="flex flex-col gap-2">
          {config.file?.url ? (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
              <FontAwesomeIcon
                icon={faFileAlt}
                className="w-5 h-5 text-white"
              />
              <div className="flex-1 text-sm text-white">
                {config.file.name ||
                  config.file.url.split("/").pop().split("?")[0]}
                {config.file.size && (
                  <span className="text-white/50 ml-2">
                    ({(config.file.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                )}
              </div>
              <label className="cursor-pointer p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                <FontAwesomeIcon
                  icon={faUpload}
                  className="w-4 h-4 text-white"
                />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.docx,.xlsx,.pptx,.txt,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                />
              </label>
            </div>
          ) : (
            <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg hover:bg-white/5 cursor-pointer transition-colors">
              <div className="flex flex-col items-center gap-2">
                <FontAwesomeIcon
                  icon={faFileAlt}
                  className="w-8 h-8 text-white/50"
                />
                <span className="text-sm text-white/70">
                  Click to upload a document
                </span>
                <span className="text-xs text-white/50">
                  PDF, DOCX, XLSX, etc. (10MB max)
                </span>
              </div>
              <input
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.docx,.xlsx,.pptx,.txt,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
              />
            </label>
          )}

          {error && <div className="text-red-500 text-sm">{error}</div>}
          {isUploading && (
            <div className="text-blue-400 text-sm">Uploading...</div>
          )}
        </div>
      </div>

      {config.file?.url && (
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-white">Show Preview</Label>
            <p className="text-xs text-white/50 mt-1">
              Enable document preview for supported file types
            </p>
          </div>
          <Switch checked={showPreview} onCheckedChange={handlePreviewToggle} />
        </div>
      )}

      <div>
        <Label className="text-white">Title</Label>
        <Input
          value={config.file?.title || ""}
          onChange={(e) => updateFile("title", e.target.value)}
          placeholder="Document title"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
        />
        <p className="mt-1 text-xs text-white/50">
          Displayed as the main title for this file. If not provided, the file
          name will be used.
        </p>
      </div>

      <div>
        <Label className="text-white">Description</Label>
        <Input
          value={config.file?.description || ""}
          onChange={(e) => updateFile("description", e.target.value)}
          placeholder="Document description"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
        />
      </div>

      <div>
        <Label className="text-white">Display Name</Label>
        <Input
          value={config.file?.name || ""}
          onChange={(e) => updateFile("name", e.target.value)}
          placeholder="Custom display name"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
        />
        <p className="mt-1 text-xs text-white/50">
          Fallback if title is not provided. Defaults to the original filename.
        </p>
      </div>
    </div>
  );
}
